import React, { useRef, useState, useCallback } from 'react';
import { uploadAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { ImagePlus, X, Loader2, Upload, GripVertical, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

// Max file size in bytes (2MB)
const MAX_FILE_SIZE = 2 * 1024 * 1024;
const MAX_DIMENSION = 1920; // Max width/height in pixels

/**
 * Resize image to fit within max dimensions and max file size
 */
const resizeImage = (file, maxSize = MAX_FILE_SIZE, maxDimension = MAX_DIMENSION) => {
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
                let { width, height } = img;
                
                // Calculate new dimensions while maintaining aspect ratio
                if (width > maxDimension || height > maxDimension) {
                    if (width > height) {
                        height = Math.round((height * maxDimension) / width);
                        width = maxDimension;
                    } else {
                        width = Math.round((width * maxDimension) / height);
                        height = maxDimension;
                    }
                }
                
                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);
                
                // Start with quality 0.9 and reduce until under max size
                let quality = 0.9;
                const tryCompress = () => {
                    canvas.toBlob(
                        (blob) => {
                            if (blob.size > maxSize && quality > 0.1) {
                                quality -= 0.1;
                                tryCompress();
                            } else {
                                // Create new file with same name
                                const resizedFile = new File([blob], file.name, {
                                    type: 'image/jpeg',
                                    lastModified: Date.now()
                                });
                                resolve(resizedFile);
                            }
                        },
                        'image/jpeg',
                        quality
                    );
                };
                tryCompress();
            };
            img.src = e.target.result;
        };
        reader.readAsDataURL(file);
    });
};

export const ImageUpload = ({ images, setImages, maxImages = 15 }) => {
    const fileInputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState('');
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);

    const handleFileSelect = async (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;

        const remainingSlots = maxImages - images.length;
        if (files.length > remainingSlots) {
            toast.error(`Poți adăuga maxim ${remainingSlots} imagini`);
            return;
        }

        setUploading(true);
        
        for (let i = 0; i < files.length; i++) {
            let file = files[i];
            setUploadProgress(`Se procesează ${i + 1}/${files.length}...`);
            
            // Validate file type
            if (!file.type.startsWith('image/')) {
                toast.error(`${file.name} nu este o imagine validă`);
                continue;
            }

            try {
                // Resize if needed (always resize to ensure < 2MB)
                if (file.size > MAX_FILE_SIZE || file.type !== 'image/jpeg') {
                    setUploadProgress(`Se redimensionează ${file.name}...`);
                    file = await resizeImage(file);
                }
                
                setUploadProgress(`Se încarcă ${file.name}...`);
                const response = await uploadAPI.uploadImage(file);
                
                if (response.data && response.data.url) {
                    // Add server URL directly (no local preview needed since upload is fast)
                    setImages(prev => [...prev, response.data.url]);
                } else {
                    toast.error(`Eroare la încărcarea ${file.name}`);
                }
            } catch (error) {
                toast.error(`Eroare la încărcarea ${file.name}`);
                console.error(error);
            }
        }

        setUploading(false);
        setUploadProgress('');
        // Reset input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const removeImage = (index) => {
        setImages(prev => prev.filter((_, i) => i !== index));
    };

    const handleFileDrop = (e) => {
        e.preventDefault();
        const files = Array.from(e.dataTransfer.files);
        if (files.length) {
            const input = fileInputRef.current;
            const dataTransfer = new DataTransfer();
            files.forEach(file => dataTransfer.items.add(file));
            input.files = dataTransfer.files;
            handleFileSelect({ target: input });
        }
    };

    // Drag and drop reordering
    const handleDragStart = (e, index) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        if (draggedIndex !== null && draggedIndex !== index) {
            setDragOverIndex(index);
        }
    };

    const handleDragEnd = () => {
        if (draggedIndex !== null && dragOverIndex !== null && draggedIndex !== dragOverIndex) {
            setImages(prev => {
                const newImages = [...prev];
                const [removed] = newImages.splice(draggedIndex, 1);
                newImages.splice(dragOverIndex, 0, removed);
                return newImages;
            });
        }
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    const moveImage = (fromIndex, direction) => {
        const toIndex = fromIndex + direction;
        if (toIndex < 0 || toIndex >= images.length) return;
        
        setImages(prev => {
            const newImages = [...prev];
            [newImages[fromIndex], newImages[toIndex]] = [newImages[toIndex], newImages[fromIndex]];
            return newImages;
        });
    };

    return (
        <div className="space-y-4">
            {/* Image Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {images.map((img, index) => (
                    <div 
                        key={`${img}-${index}`}
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDragEnd={handleDragEnd}
                        className={cn(
                            "relative aspect-square rounded-xl overflow-hidden group bg-gray-100 cursor-move transition-all",
                            draggedIndex === index && "opacity-50 scale-95",
                            dragOverIndex === index && "ring-2 ring-primary ring-offset-2"
                        )}
                    >
                        <img 
                            src={img} 
                            alt={`Imagine ${index + 1}`} 
                            className="w-full h-full object-cover"
                            draggable={false}
                        />
                        
                        {/* Drag handle */}
                        <div className="absolute top-2 left-2 w-7 h-7 rounded-full bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <GripVertical className="w-4 h-4" />
                        </div>
                        
                        {/* Remove button */}
                        <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute top-2 right-2 w-7 h-7 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-red-600"
                        >
                            <X className="w-4 h-4" />
                        </button>
                        
                        {/* Reorder buttons */}
                        <div className="absolute bottom-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            {index > 0 && (
                                <button
                                    type="button"
                                    onClick={() => moveImage(index, -1)}
                                    className="w-6 h-6 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </button>
                            )}
                            {index < images.length - 1 && (
                                <button
                                    type="button"
                                    onClick={() => moveImage(index, 1)}
                                    className="w-6 h-6 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70"
                                >
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                        
                        {/* Primary badge */}
                        {index === 0 && (
                            <span className="absolute bottom-2 left-2 px-2 py-0.5 rounded-full bg-primary text-white text-xs font-medium">
                                Principal
                            </span>
                        )}
                    </div>
                ))}

                {/* Upload Button */}
                {images.length < maxImages && (
                    <div
                        onDrop={handleFileDrop}
                        onDragOver={(e) => e.preventDefault()}
                        className={cn(
                            "aspect-square rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-2 cursor-pointer transition-all",
                            uploading 
                                ? "border-primary bg-blue-50" 
                                : "border-border hover:border-primary hover:bg-blue-50/50"
                        )}
                        onClick={() => !uploading && fileInputRef.current?.click()}
                    >
                        {uploading ? (
                            <>
                                <Loader2 className="w-8 h-8 text-primary animate-spin" />
                                <span className="text-xs text-primary text-center px-2">
                                    {uploadProgress || 'Se încarcă...'}
                                </span>
                            </>
                        ) : (
                            <>
                                <ImagePlus className="w-8 h-8 text-muted-foreground" />
                                <span className="text-xs text-muted-foreground text-center px-2">
                                    Click sau drag & drop
                                </span>
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* Hidden file input - uses standard file picker */}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                multiple
                onChange={handleFileSelect}
                className="hidden"
            />

            {/* Info */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-sm text-muted-foreground">
                <span>{images.length} / {maxImages} imagini • Trage pentru a reordona</span>
                <span>Max 10MB per imagine • JPEG, PNG, WebP, GIF</span>
            </div>
        </div>
    );
};

export default ImageUpload;
